import React, { useState } from 'react';
import { Skeleton } from 'antd';
import { currencyFormatter } from '../../../../../shared/components/ui/atoms';
import { FooterContent } from '../../../../onboarding/components';
import { SendLoanFormInner } from '../../SendLoanForm/SendLoanForm';
import { InfoTooltip } from './Tooltip';
import { AdjustLTVAmount } from './AdjustLTVAmount';
import { isShortTermFixAndFlip, cannotBePriced } from '../../../../../utils/functions';
import { toUSD } from '../../../../../utils/constants';

const TitleLoanBuilder = () => (
  <div className='loan-rate'>
    <h1>Estimate your rate</h1>
  </div>
)

const LoanResumeLoanBuilder = (props: any) => {
  const { values, setValue, isBrokerMode } = props

  return (
    <div className='resume-container'>
      <FooterContent
        values={values}
        setValue={setValue}
        oneLine={true}
        firstPerson
        isBrokerMode={isBrokerMode}
        forceUnlockCreditInfo
      />
    </div>
  )
}

const NoLoanOptionsMobile = ({ errors }: { errors: any[] }) => (
  <div className='no-loan-options'>
    <h1>No loan options available</h1>
    <p>Please fix the following or contact us at <a href="mailto:lend@constlending.com">lend@constlending.com</a></p>
    {
      errors && errors.length > 0 ?
        <ul>
          <li>{errors[0]}</li>
        </ul>
        :
        null
    }
  </div>
)

const LoanDetailsMobile = (props: any) => {
  let { ltv, arv_ltv, ltc, isLoading, loanBalance, values, loanCantBeAutopriced } = props;

  ltv = isNaN(ltv) ? 0 : ltv
  arv_ltv = isNaN(arv_ltv) ? 0 : arv_ltv
  ltc = isNaN(ltc) ? 0 : ltc
  loanBalance = isNaN(loanBalance) || loanCantBeAutopriced ? 0 : loanBalance

  return (
    <div className='loan-details-mobile'>
      <div className='loan-details'>
        {
          isShortTermFixAndFlip(values) ?
            <div>
              <div className='arv'>
                <div>
                  <span>
                    ARV LTV{" "}
                    <InfoTooltip title={"To adjust ARV LTV, change Desired as-is LTV at the bottom"} placement={"top"} variant="gray" isDisabled={false} />
                  </span>
                  <span>
                    {
                      isLoading ?
                        <Skeleton.Input />
                        :
                        `${(arv_ltv * 100).toFixed(0)}%`
                    }
                  </span>
                </div>
                <div>
                  <span>
                    LTC{" "}
                    <InfoTooltip title={"To adjust LTC, change Desired as-is LTV at the bottom"} placement={"top"} variant="gray" isDisabled={false} />
                  </span>
                  <span>
                    {
                      isLoading ?
                        <Skeleton.Input />
                        :
                        `${(ltc * 100).toFixed(1)}%`
                    }
                  </span>
                </div>
              </div>
            </div>
            :
            <div>
              <span className='ltv-label'>
                LTV{" "}
                <InfoTooltip title={"To adjust LTV, change the numbers at the bottom or use the slider"} placement={"top"} variant="gray" isDisabled={false} />
              </span>
              <span>
                {
                  isLoading ?
                    <Skeleton.Input />
                    :
                    <React.Fragment>
                      <span>{`${(ltv * 100).toFixed(0)}%`}</span>
                    </React.Fragment>
                }
              </span>
            </div>
        }
        <div>
          <span>
            Loan amount
          </span>
          <span>
            <React.Fragment>
              {
                isLoading ?
                  <Skeleton.Input />
                  :
                  <span>{`$${currencyFormatter(loanBalance).split(".")[0]}`}</span>
              }
            </React.Fragment>
          </span>
        </div>
      </div>
      {
        isShortTermFixAndFlip(values) ?
          <div className='loan-details-caption'>
            Use bottom slider to adjust as-is LTV
          </div>
          :
          null
      }
    </div>
  )
}

const InputList = (props: any) => {
  let { inputElements, values, isLoading, loanCantBeAutopriced } = props;

  inputElements = [...inputElements]

  if (!isLoading) {
    if (isShortTermFixAndFlip(values)) {
      if (!loanCantBeAutopriced) {
        inputElements.pop()
      }
      inputElements.push(
        loanCantBeAutopriced ?
          null
          :
          <div key="desired-as-is-ltv" className='loan-input placeholder'>
            <p>Desired as-is LTV</p>
            <AdjustLTVAmount {...props} clean />
          </div>
      );
    } else {
      inputElements.push(
        loanCantBeAutopriced ?
          null
          :
          <div key="adjust-ltv" className='loan-input placeholder'>
            <p>ADJUST LTV</p>
            <AdjustLTVAmount {...props} clean />
          </div>
      );
    }
  }

  return (
    <div className='loan-inputs'>
      {inputElements}
    </div>
  )
}

const SelectOptionsMobile = (props: any) => {
  const { isLoading, onSelect } = props
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0)

  const options = [...props.options]
  options.reverse()
  const selectedOption = options[selectedOptionIndex]

  const { rate, monthlyPayment, originationPoints } = selectedOption || {}

  const showSkeleton = isLoading || !selectedOption

  const getOptionItemClassName = (currentIndex: number, selectedIndex: number) => {
    return currentIndex === selectedIndex ? 'option-item selected' : 'option-item';
  };

  const optionListItems = [
    "LOWEST CASH",
    "BALANCED OPTION",
    "BEST RATE",
  ].map((label, i) => (
    <div key={i} className={getOptionItemClassName(i, selectedOptionIndex)} onClick={() => setSelectedOptionIndex(i)}>{label}</div>
  ))

  return (
    <div className='option-selector'>
      <div className='option-list'>
        {optionListItems}
      </div>
      <div className='option-selected'>
        <div className='option-details'>
          <div className='rate'>
            <div>
              <span>Rate</span>
              {
                showSkeleton ?
                  <Skeleton.Input />
                  :
                  <span>{rate}%</span>
              }
            </div>
          </div>
          <div className='monthly-payment'>
            <div>
              <span>Monthly Payment <InfoTooltip title={"This doesn't include taxes and insurance"} variant={"gray"} isDisabled={false} /></span>
              {
                showSkeleton ?
                  <Skeleton.Input />
                  :
                  <span>{toUSD(monthlyPayment)}</span>
              }
            </div>
          </div>
          <div className='buydown'>
            <div>
              <span>Buydown <InfoTooltip title={"Pay a percentage of the loan amount upfront to decrease the interest rate over the life of the loan"} variant={"gray"} isDisabled={false} /></span>
              {
                showSkeleton ?
                  <Skeleton.Input />
                  :
                  <span>{Number(originationPoints).toFixed(2)}%</span>
              }
            </div>
          </div>
        </div>
        <div
          className='option-select-button'
          onClick={() => onSelect(
            {
              ...selectedOption,
              nonSelectedOptions: [0,1,2].filter(i => i !== selectedOptionIndex).map(i => options[i])
            }
          )}
        >
          Select this rate
        </div>
      </div>
    </div>
  )
}

interface MobileLoanBuilderProps {
  isLoading: boolean;
  values: any;
  setValue: (key: string, value: any) => void;
  isBrokerMode?: boolean;
  inputElements: React.ReactNode[];
  options: any[];
  onSelect: (option: any) => void;
  min_ltv?: number;
  max_ltv?: number;
  ltv: number;
  setLtv: (value: number) => void;
  arv_ltv?: number;
  ltc?: number;
  loanBalance: number;
  maxLTVPosition: string | null;
  setMaxLTVPosition: (value: string | null) => void;
  loanCantBeAutopriced: boolean;
  isPricerDisabled: boolean;
  errors: any[];
}

export const MobileLoanBuilder = (props: MobileLoanBuilderProps) => {
  const className = props.isPricerDisabled ? 'loan-builder disabled' : 'loan-builder'

  return (
    <div className={`${className} loan-builder-mobile`}>
      <div className='content'>
        <TitleLoanBuilder />
        <LoanResumeLoanBuilder {...props} />
        {
          props.loanCantBeAutopriced && cannotBePriced(props.values) ?
            <SendLoanFormInner data={{...props.values}}/>
            :
            <React.Fragment>
              <LoanDetailsMobile {...props} />
              <InputList {...props}/>
              <div className='input-space' />
            </React.Fragment>
        }
      </div>
      {
        props.loanCantBeAutopriced ?
          (
            !cannotBePriced(props.values) ?
              <NoLoanOptionsMobile {...props} />
              :
              null
          )
          :
          <SelectOptionsMobile {...props} />
      }
    </div>
  )
}

